import { DataSet, FetchStatus, IDataSource, IEntity, IMultipleEntities, IQueryOptions, QueryType } from "../../../base/types";
import { DataSourceProvider, useDataSource } from "./DataSourceProvider";
import { createContext, useContext, useEffect, useReducer } from "react"

import { useTableDataQuery } from "./useTableDataQuery.hook";

type ActionType = { type: 'SET_DATA'; payload: DataSet }

type DataContextProps = DataSet & {
    dataSource?: IDataSource,
    setDataSource: (dataSource: IDataSource) => void;

    setData: (data: DataSet) => void;
    fetch: (queryOptions: IQueryOptions) => Promise<IEntity> | Promise<IMultipleEntities>,
    goto: (pageNo: number) => void,
    refetch: () => Promise<void>;
    saveData: (options: { values: IEntity, systemId?: string }, postSaveData?: () => Promise<void>) => Promise<IEntity>;

    queryOptions?: IQueryOptions,
};

const context = createContext<DataContextProps>({
    type: 'Static',
    status: FetchStatus.Idle,
    setData: () => { },
    setDataSource: () => { },
    fetch: () => Promise.reject(),
    goto: () => { },
    refetch: () => Promise.reject(),
    saveData: () => Promise.reject(),
});

const initialState: DataSet = {
    type: 'Static',
    status: FetchStatus.Idle
};

const dataContextReducer = (state: DataSet, action: ActionType): DataSet => {
    switch (action.type) {
        case 'SET_DATA':
            return { ...action.payload };
        default:
            return state;
    }
};

const DataContextProviderInner = ({ children }: { children?: React.ReactNode }) => {
    const [data, dispatch] = useReducer(dataContextReducer, initialState);
    const { dataSource, setDataSource } = useDataSource();
    const { queryOptions, fetch, refetch, goto, saveData: saveTableData, data: tableData } = useTableDataQuery()

    useEffect(() => {
        if (!tableData || !queryOptions)
            return;

        const { queryType } = queryOptions;
        switch (queryType) {
            case QueryType.Record:
            case QueryType.MultipleRecords:
                handleState(queryOptions.queryType, Promise.resolve(tableData))
                break;
            case QueryType.FirstRecord:
                const records = tableData?.records || [];
                const record = Array.isArray(records) ? (records[0] || undefined) : records;
                handleState(queryOptions.queryType, Promise.resolve(record))
                break;
        }

        // eslint-disable-next-line
    }, [tableData]);

    const handleState = (queryType: QueryType, promise: Promise<IEntity> | Promise<IMultipleEntities>) => {
        dispatch({ type: 'SET_DATA', payload: { type: 'Static', status: FetchStatus.Loading } });
        return promise
            .then(data => {
                switch (queryType) {
                    case QueryType.Record:
                    case QueryType.FirstRecord:
                        dispatch({ type: 'SET_DATA', payload: { type: "Card", result: data, status: FetchStatus.Success } });
                        break;
                    case QueryType.MultipleRecords:
                        dispatch({ type: 'SET_DATA', payload: { type: "List", result: data as IMultipleEntities, status: FetchStatus.Success } });
                        break;
                }
                return data;
            })
            .catch(err => {
                console.error(err);
                const message = err.data?.error?.message || 'Failed to get data';
                dispatch({ type: 'SET_DATA', payload: { type: 'Static', status: FetchStatus.Error, error: message } });
                return Promise.reject(message);
            });
    }

    const fetchQuery = (queryOptions: IQueryOptions) => {
        return fetch(queryOptions)
    }

    const refetchQuery = () => {
        if (!queryOptions)
            return Promise.reject()

        const promise = refetch()
        return handleState(queryOptions.queryType, promise)
            .then(() => Promise.resolve())
    }

    const gotoPageQuery = (pageNo: number) => {
        if (!queryOptions)
            return Promise.reject()

        const promise = goto(pageNo)
        return handleState(queryOptions.queryType, promise)
    }

    const setData = (newData: DataSet) => {
        dispatch({ type: 'SET_DATA', payload: newData });
    }

    const saveData = (options: { values: IEntity, systemId?: string }, postSaveData?: () => Promise<void>) => {
        return saveTableData(options)
            .then(data => {
                if (postSaveData) {
                    return postSaveData()
                        .then(() => {
                            dispatch({ type: 'SET_DATA', payload: { type: "Card", result: data, status: FetchStatus.Success } });
                            return data;
                        })
                } else {
                    dispatch({ type: 'SET_DATA', payload: { type: "Card", result: data, status: FetchStatus.Success } });
                    return data;
                }
            })
            .catch(err => {
                const message = typeof err === 'string' ? err : err.data?.error?.message || 'Failed to save data';
                dispatch({ type: 'SET_DATA', payload: { type: 'Static', status: FetchStatus.Error, error: message } });
                return Promise.reject(message);
            });
    }

    return (
        <context.Provider
            value={{
                dataSource,
                setDataSource,
                ...data,
                setData,
                fetch: fetchQuery,
                refetch: refetchQuery,
                goto: gotoPageQuery,
                saveData,
                queryOptions,
            }}
        >
            {children}
        </context.Provider>
    );
};

export const DataContextProvider = ({ children }: { children?: React.ReactNode }) => {
    return <>
        <DataSourceProvider>
            <DataContextProviderInner>
                {children}
            </DataContextProviderInner>
        </DataSourceProvider>
    </>
}

export function useDataContext() {
    return useContext(context);
}
